import React, { useState, useEffect } from "react";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import ShiftLogRoot from "examples/ShiftLog/ShiftLogRoot";
import clipbord from "assets/images/clipbord_image.jpeg";
import { api } from "utils/Api";
import {
  useSoftUIController,
  setOpenShiftLog,
  closePanels,
} from "context";
import Swal from "sweetalert2";
import { getSecureItem } from "utils/SecureStorage";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";
import { canCreate } from "utils/permissionManager";

function ShiftLog() {
  const [controller, dispatch] = useSoftUIController();
  const { openShiftLog } = controller;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [shiftLogs, setShiftLogs] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  const [shiftLogHeight, setShiftLogHeight] = useState(0);
  const [addNoteHeight, setAddNoteHeight] = useState(0);
  const { propertyId, orgId } = useHierarchy();
  // Get logged-in user's ID 
  const currentUser = JSON.parse(getSecureItem("currentUser"));
  const loggedInUserId = currentUser?.id || null;
  const currentUserOrganizationId = currentUser?.organization_id || null;
  const current_user_role_id = getSecureItem("role_id");

  useEffect(() => {
    const handleClickOutside = (event) => {
      const panel = document.getElementById("shift-log-panel");

      if (panel && !panel.contains(event.target)) {
        closePanels(dispatch);
      }
    };

    if (openShiftLog) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openShiftLog, dispatch]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const getUserName = (userId) => {
    const user = usersMap[Number(userId)];
    const userName = user?.name || "";
    return userName;
  };

  const fetchUsers = async () => {
    try {
      const payload = {
        organization_id: orgId ||currentUserOrganizationId,
        property_id: parseInt(propertyId),
      }
      const res = await api.user.getAllUserWithAdmin(payload);
      const map = {};
      if (Array.isArray(res)) {
        res.forEach((u) => {
          map[u.id] = u;
        });
      }
      setUsersMap(map);
    } catch (error) {
      
    }
  };

  // ─── Fetch ALL shift logs ───────────────────────────────────────
  const fetchShiftLogs = async () => {
    setIsFetching(true);
    try {
      const payload = {
        organization_id: orgId || currentUserOrganizationId,
        property_id: parseInt(propertyId),
      };
      const response = await api.shiftNote.getShiftNote(payload);
      setShiftLogs(response || []);
    } catch (error) {
      
      setShiftLogs([]);
    } finally {
      setIsFetching(false);
    }
  };

  // ─── Load everything when component opens ───────────────────────
  useEffect(() => {
    if (openShiftLog) {
      fetchUsers();
      fetchShiftLogs();
    }
  }, [openShiftLog]);

  // ─── Submit handler ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!noteText.trim()) return;

    setIsLoading(true);

    const payload = {
      message: noteText,
      property_id: parseInt(propertyId),
      frontend_metadata: {},
      backend_metadata: {},
      organization_id: orgId || currentUserOrganizationId,
    };

    try {
      await api.shiftNote.createShiftNote(payload);
      setNoteText("");
      // showSuccess("Shift Note Added Successfully");
      await fetchShiftLogs();
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Layout measurement ────────────────────────────────────────
  useEffect(() => {
    const measure = () => {
      const shiftLogContainer = document.getElementById("shift-log-container");
      const addNoteContainer = document.getElementById("add-note-container");

      if (shiftLogContainer) {
        setShiftLogHeight(shiftLogContainer.getBoundingClientRect().height);
      }
      if (addNoteContainer) {
        setAddNoteHeight(addNoteContainer.getBoundingClientRect().height);
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handleCloseShiftLog = () => closePanels(dispatch);

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <ShiftLogRoot variant="permanent" ownerState={{ openShiftLog }}>
      <SoftBox
        id="shift-log-panel"
        style={{
          height: "100vh",
          border: "10px solid #a9702c",
          borderRadius: "15px",
          marginTop: "25px",
        }}
      >
        {/* ── Header ── */}
        <SoftBox
          display="flex"
          flexWrap="nowrap"
          flexDirection="column-reverse"
          alignContent="center"
          justifyContent="flex-end"
          alignItems="center"
          marginTop="-40px"
        >
          <img
            style={{ marginTop: "-32px", transform: "scale(0.8)" }}
            src={clipbord}
            alt="clipboard"
          />
          <Icon
            sx={({ typography: { size, fontWeightBold } }) => ({
              fontSize: `${size.md} !important`,
              fontWeight: `${fontWeightBold} !important`,
              strokeWidth: "2px",
              cursor: "pointer",
              mt: 2,
              zIndex: "999",
              color: "red",
            })}
            style={{ backgroundColor: "beige", borderRadius: "10px" }}
            onClick={handleCloseShiftLog}
          >
            close
          </Icon>
        </SoftBox>

        <SoftBox>
          {/* ── Fixed top section ── */}
          <SoftBox
            id="shift-log-container"
            style={{
              lineHeight: "20px",
              display: "flex",
              padding: "5px",
              flexDirection: "column",
              position: "fixed",
              width: "95%",
              backgroundColor: "white",
              marginTop: "-15px",
            }}
          >
            <SoftTypography variant="h5" style={{ textAlign: "center" }}>
              Shift Notes
            </SoftTypography>
            <hr />
            <h5>
              Shift Notes :
              {isFetching && (
                <span style={{ fontWeight: "normal", fontSize: "12px", marginLeft: "8px" }}>
                  Loading...
                </span>
              )}
            </h5>
          </SoftBox>

          {/* ── Scrollable logs list ── */}
          <SoftBox
            style={{
              padding: "10px",
              position: "fixed",
              overflow: "hidden",
              top: shiftLogHeight + 75,
              bottom: addNoteHeight,
              width: "95%",
              boxSizing: "border-box",
              overflowY: "auto",
            }}
          >
            <hr />

            {!isFetching && shiftLogs.length === 0 && (
              <p
                style={{
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#888",
                  marginTop: "20px",
                }}
              >
                No shift notes available.
              </p>
            )}

            {shiftLogs.map((log) => {
              const userName = getUserName(log?.user_id);

              return (
                <React.Fragment key={log?.id}>
                  <p style={{ fontSize: "15px", whiteSpace: "normal" }}>
                    <span style={{ color: "#888", fontSize: "13px" }}>
                      {formatDate(log?.created_at)}
                    </span>{" "}
                    — {log?.message} -{" "}
                    <span style={{ fontWeight: "600", color: "#333" }}>{userName}</span>
                  </p>
                  <hr />
                </React.Fragment>
              );
            })}
          </SoftBox>

          {/* ── Fixed bottom form (simplified) ── */}
          {canCreate("Shift Note") && (
            <SoftBox
              id="add-note-container"
              style={{
                padding: "5px",
                position: "fixed",
                width: "95%",
                bottom: "10px",
              }}
            >
              <hr />
              <h5>Add Note :</h5>

              <form onSubmit={handleSubmit}>
                <textarea
                  style={{
                    width: "100%",
                    height: "60px",
                    marginBottom: "10px",
                    padding: "8px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    resize: "vertical",
                    fontSize: "14px",
                  }}
                  name="message"
                  placeholder="Type your message here..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <SoftButton
                    color="primary"
                    type="submit"
                    disabled={isLoading || !noteText.trim()}
                  >
                    {isLoading ? "Adding..." : "+ Add Note"}
                  </SoftButton>
                </div>
              </form>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </ShiftLogRoot>
  );
}

export default ShiftLog;